/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import EventToolsBackButton from "./EventToolsBackButton";
import EventToolsSkeleton from "./EventToolsSkeletons";

const formatDateTime = (isoValue, locale) => {
  if (!isoValue) return "—";
  const dt = new Date(isoValue);
  if (Number.isNaN(dt.getTime())) return "—";

  return dt.toLocaleString(locale || undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PHASE_TONE_CLASSES = {
  success: {
    dot: "border-emerald-300/45 bg-emerald-400",
    text: "text-emerald-200",
  },
  upcoming: {
    dot: "border-blue-300/45 bg-blue-400",
    text: "text-blue-200",
  },
  warning: {
    dot: "border-amber-200/45 bg-amber-300",
    text: "text-amber-100",
  },
  muted: {
    dot: "border-white/25 bg-white/35",
    text: "text-white/58",
  },
};

const PlansSentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
    <path
      d="m7 12.5 3.1 3.1L17.5 8"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TimelineStep = ({
  title,
  status,
  tone = "muted",
  detail = "",
  isLast = false,
}) => {
  const toneClasses = PHASE_TONE_CLASSES[tone] || PHASE_TONE_CLASSES.muted;

  return (
    <div className="relative flex gap-3">
      {!isLast ? (
        <span
          className="absolute bottom-[-14px] left-[7px] top-[19px] w-px bg-white/12"
          aria-hidden="true"
        />
      ) : null}
      <span
        className={`relative mt-[5px] h-[15px] w-[15px] shrink-0 rounded-full border-[4px] ${toneClasses.dot}`}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="font-semibold leading-5 text-white">{title}</div>
        <div className={`mt-0.5 text-[13px] font-medium leading-5 ${toneClasses.text}`}>
          {status}
        </div>
        {detail ? (
          <p className="mt-1 text-[12px] leading-[18px] text-white/58">{detail}</p>
        ) : null}
      </div>
    </div>
  );
};

const HostIntentionRequestModal = ({
  visible = false,
  eventId = null,
  onClose = () => {},
  onSent = null,
  onBack = null,
  embedded = false,
  paymentRequest = false,
}) => {
  const { t, i18n } = useTranslation("events");

  const [loading, setLoading] = React.useState(false);
  const [statusLoaded, setStatusLoaded] = React.useState(false);
  const [localError, setLocalError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [statusInfo, setStatusInfo] = React.useState(null);
  const [confirmingImmediateSend, setConfirmingImmediateSend] = React.useState(false);
  const copyPrefix = `events.details.more_actions.${paymentRequest || statusInfo?.request_kind === "payment" ? "payment_modal" : "intention_modal"}`;

  const loadStatus = React.useCallback(async () => {
    if (!eventId) {
      setStatusLoaded(true);
      return;
    }
    setLoading(true);
    setStatusLoaded(false);
    setLocalError("");
    try {
      const { data } = await api.get(`/events/${eventId}/host-intention-request/`);
      setStatusInfo(data || null);
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("events.details.generic_error");
      setLocalError(msg);
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg, status } })
      );
    } finally {
      setLoading(false);
      setStatusLoaded(true);
    }
  }, [eventId, t]);

  React.useEffect(() => {
    if (!visible || !eventId) return;
    void loadStatus();
  }, [eventId, loadStatus, visible]);

  React.useEffect(() => {
    if (visible) return;
    setLoading(false);
    setStatusLoaded(false);
    setLocalError("");
    setSubmitting(false);
    setStatusInfo(null);
    setConfirmingImmediateSend(false);
  }, [visible]);

  if (!visible) return null;
  const paymentRequestSupported = !paymentRequest || statusInfo?.request_kind === "payment";
  const canSendNow = Boolean(statusInfo?.can_send_now && paymentRequestSupported);


  const shouldShowFollowupBlock = Boolean(
    statusInfo?.intent_already_sent || canSendNow
  );

  const blockReasonText = statusInfo?.send_block_reason_code
    ? t(
        `${copyPrefix}.block_reasons.${statusInfo.send_block_reason_code}`,
        { defaultValue: statusInfo.send_block_reason || "" }
      )
    : "";

  const getPhasePresentation = (phase, phaseType) => {
    if (!phase) {
      return {
        status: "—",
        tone: "muted",
      };
    }

    const datetime = formatDateTime(phase.scheduled_at, i18n.language);
    const isCleanup = phaseType === "cleanup";

    if (phase.completed) {
      return {
        status: t(
          isCleanup
            ? `${copyPrefix}.cleanup_phase_started`
            : `${copyPrefix}.phase_sent`
        ),
        tone: "success",
      };
    }

    if (phase.projected) {
      return {
        status: t(
          isCleanup
            ? `${copyPrefix}.cleanup_phase_projected`
            : `${copyPrefix}.phase_projected`,
          { datetime }
        ),
        tone: "muted",
      };
    }

    if (phase.status === "scheduled") {
      return {
        status: t(
          isCleanup
            ? `${copyPrefix}.cleanup_phase_scheduled`
            : `${copyPrefix}.phase_scheduled`,
          { datetime }
        ),
        tone: "upcoming",
      };
    }

    return {
      status: t(
        isCleanup
          ? `${copyPrefix}.cleanup_phase_processing`
          : `${copyPrefix}.phase_processing`,
        { datetime }
      ),
      tone: "warning",
    };
  };

  const renderFollowupTimeline = () => {
    const reminderPhase = getPhasePresentation(statusInfo?.second_reminder, "reminder");
    const cleanupPhase = statusInfo?.allow_auto_cleanup
      ? getPhasePresentation(statusInfo?.cleanup, "cleanup")
      : {
          status: t(
            `${copyPrefix}.cleanup_phase_disabled`
          ),
          tone: "muted",
        };
    const usesSmartCleanup = statusInfo?.cleanup_mode === "smart";
    const cleanupDetail = statusInfo?.allow_auto_cleanup
      ? t(
          usesSmartCleanup
            ? `${copyPrefix}.release_detail_smart`
            : `${copyPrefix}.release_detail_hard`,
          { count: statusInfo?.cleanup_buffer ?? 2 }
        )
      : t(`${copyPrefix}.auto_cleanup_off`);

    return (
      <div className="rounded-[20px] border border-white/10 bg-white/[0.055] p-4">
        <div className="mb-4 text-[12px] font-semibold uppercase tracking-[0.14em] text-white/48">
          {t(`${copyPrefix}.timeline_title`)}
        </div>
        <div className="space-y-4">
          <TimelineStep
            title={t(`${copyPrefix}.second_title`)}
            status={reminderPhase.status}
            tone={reminderPhase.tone}
          />
          <TimelineStep
            title={t(`${copyPrefix}.cleanup_title`)}
            status={cleanupPhase.status}
            tone={cleanupPhase.tone}
            detail={cleanupDetail}
            isLast
          />
        </div>
      </div>
    );
  };

  const handleSubmit = async () => {
    if (!eventId || submitting || !canSendNow) return;

    setSubmitting(true);
    try {
      const { data } = await api.post(`/events/${eventId}/host-intention-request/`);
      if (data?.status_info) {
        setStatusInfo(data.status_info);
      }

      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message: t(`${copyPrefix}.success`),
          },
        })
      );

      if (typeof onSent === "function") {
        await onSent(data?.status_info || null);
      }
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("events.details.generic_error");

      if (err?.response?.data?.status_info) {
        setStatusInfo(err.response.data.status_info);
      }

      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg, status } })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const canRequestImmediately = Boolean(
    canSendNow && !statusInfo?.intent_already_sent
  );
  const isStatusLoading = loading || !statusLoaded;

  const footerContent = confirmingImmediateSend ? (
    <div className="flex gap-3">
      <AppModalButton
        variant="secondary"
        onClick={() => setConfirmingImmediateSend(false)}
        disabled={submitting}
        className="flex-1"
      >
        {t(`${copyPrefix}.confirm_cancel`)}
      </AppModalButton>
      <AppModalButton
        onClick={handleSubmit}
        disabled={submitting || loading || !statusInfo?.can_send_now}
        loading={submitting}
        className="flex-1 text-[#2563EB]"
      >
        {submitting
          ? t(`${copyPrefix}.sending`)
          : t(`${copyPrefix}.confirm_send`)}
      </AppModalButton>
    </div>
  ) : canRequestImmediately ? (
    <AppModalButton
      onClick={() => setConfirmingImmediateSend(true)}
      disabled={submitting || loading || !statusInfo?.can_send_now}
      className="text-[#2563EB]"
    >
      {t(`${copyPrefix}.send`)}
    </AppModalButton>
  ) : (
    null
  );

  const bodyContent = statusInfo && confirmingImmediateSend ? (
    <div className="space-y-4 text-sm">
      {embedded ? (
        <h3 className="text-lg font-semibold text-white">
          {t(`${copyPrefix}.confirm_send_title`)}
        </h3>
      ) : null}
      <p className="text-white/90">
        {t(`${copyPrefix}.confirm_send_body`, {
          count: statusInfo.pending_participants ?? 0,
          deadline: formatDateTime(statusInfo.manual_payment_deadline_at, i18n.language),
        })}
      </p>
      {statusInfo.automatic_request_at ? (
        <p className="text-white/70">
          {t(`${copyPrefix}.confirm_send_schedule`, {
            datetime: formatDateTime(statusInfo.automatic_request_at, i18n.language),
          })}
        </p>
      ) : null}
    </div>
  ) : statusInfo ? (
    <div className="space-y-4 text-sm">
      {!paymentRequestSupported ? <p className="text-white/70">{t(`${copyPrefix}.unavailable`)}</p> : null}
      {statusInfo.intent_already_sent ? (
        <>
          <div className="rounded-[20px] border border-emerald-300/20 bg-emerald-400/[0.09] p-4">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-400/18 text-emerald-200">
                <PlansSentIcon />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-medium leading-6 text-white/88">
                  {t(
                    `${copyPrefix}.status_already_sent_prefix`
                  )}{" "}
                  <strong className="inline rounded-md bg-emerald-300/14 px-1.5 py-0.5 font-semibold text-emerald-100">
                    {t(
                      `${copyPrefix}.status_already_sent_highlight`
                    )}
                  </strong>
                </p>
                <p className="mt-2 text-[12px] font-medium leading-5 text-emerald-100/72">
                  {t(`${copyPrefix}.first_sent_at`, {
                    datetime: formatDateTime(
                      statusInfo.intent_sent_at,
                      i18n.language
                    ),
                  })}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-[18px] border border-white/10 bg-black/10 px-4 py-3.5">
            <div className="min-w-0">
              <div className="font-semibold text-white">
                {t(`${copyPrefix}.confirmed_title`)}
              </div>
              <div className="mt-0.5 text-[12px] leading-[18px] text-white/55">
                {t(`${copyPrefix}.confirmed_help`)}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-[22px] font-semibold leading-none text-white">
                {statusInfo.confirmed_participants ??
                  Math.max(
                    0,
                    (statusInfo.participants_total ?? 0) -
                      (statusInfo.pending_participants ?? 0)
                  )}
              </div>
              <div className="mt-1 text-[11px] font-medium text-white/48">
                {t(`${copyPrefix}.confirmed_of_total`, {
                  total: statusInfo.participants_total ?? 0,
                })}
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <p className="text-[15px] leading-6 text-white/82">
            {t(`${copyPrefix}.intro_can_send`)}
          </p>

          {statusInfo.automatic_request_at ? (
            <div className="rounded-[18px] border border-blue-300/15 bg-blue-400/[0.07] px-4 py-3.5">
              <div className="text-[12px] font-semibold uppercase tracking-[0.13em] text-blue-100/55">
                {t(
                  `${copyPrefix}.automatic_send_label`
                )}
              </div>
              <div className="mt-1 text-[15px] font-semibold leading-6 text-white">
                {formatDateTime(
                  statusInfo.automatic_request_at,
                  i18n.language
                )}
              </div>
              <div className="mt-1 text-[12px] leading-[18px] text-white/58">
                {t(
                  `${copyPrefix}.selected_schedule`,
                  { days: statusInfo.intent_days_before ?? 3 }
                )}
              </div>
            </div>
          ) : null}
        </>
      )}

      {!statusInfo.intent_already_sent && statusInfo?.send_block_reason ? (
        <p className="rounded-[16px] border border-amber-200/15 bg-amber-300/[0.07] px-3 py-2.5 font-medium leading-5 text-amber-100">
          {t(`${copyPrefix}.blocked_prefix`, {
            reason: blockReasonText,
          })}
        </p>
      ) : null}

      {shouldShowFollowupBlock ? renderFollowupTimeline() : null}

      {!statusInfo.intent_already_sent && canSendNow ? (
        <p className="text-[13px] leading-5 text-white/62">
          {t(`${copyPrefix}.send_early_hint`)}
        </p>
      ) : null}
    </div>
  ) : localError ? (
    <div className="rounded-[18px] border border-red-300/25 bg-red-500/18 px-3 py-2 text-sm font-medium text-red-50">
      {localError}
    </div>
  ) : (
    <EventToolsSkeleton variant="intention" />
  );

  if (embedded) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {isStatusLoading ? (
          <EventToolsSkeleton variant="intention" />
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">{bodyContent}</div>
            {footerContent ? <div className="mt-5 shrink-0">{footerContent}</div> : null}
          </>
        )}
      </div>
    );
  }

  return (
    <AppModalShell
      open={visible}
      title={t(
        confirmingImmediateSend
          ? `${copyPrefix}.confirm_send_title`
          : `${copyPrefix}.title`
      )}
      onClose={onClose}
      closeAriaLabel={t(`${copyPrefix}.close`)}
      zIndexClassName="z-[1200]"
      maxWidthClassName="max-w-[520px]"
      leftAction={
        typeof onBack === "function" ? (
          <EventToolsBackButton
            onClick={onBack}
            label={t("events.details.more_actions.back")}
          />
        ) : null
      }
      footer={footerContent}
    >
      {isStatusLoading ? <EventToolsSkeleton variant="intention" /> : bodyContent}
    </AppModalShell>
  );
};

export default HostIntentionRequestModal;
